import React from 'react';

import t from '../../../labels/labelling-utils';
import { RACE_STATUS_PLAYING, RaceSessionStatus } from '../../../typings/race-session';
import { CIRCUITTRACK, DRIVERAVATAR, GPFLAG, placeholdersMap } from '../../../utils/placeholders-motorsports';
import { makeClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import Avatar from '../../atoms/avatar/avatar';
import BestLapComponent from '../../atoms/best-lap/best-lap';
import ImgPlaceholder from '../../atoms/img-placeholder/img-placeholder';
import Text from '../../atoms/text/text';
import { IDriver } from '../../molecules/team-drivers-card/team-drivers-card';

import './motorsports-hero.scss';

export interface IMotorsportsHero {
  flagUrl: string;
  customGpName?: string;
  circuitUrl?: string;
  startDateTime: string;
  winner?: IDriver; // TODO: TBD BE, will pass NULL when it does not exist
  gpStatus?: RaceSessionStatus;
  laps: string;
  lapRecord: string;
  lengthKm: string;
}

const getstatisticsLabelMap = () => {
  return {
    laps: t('motorsports.hero.statistics.laps.total'),
    lapRecord: t('motorsports.hero.statistics.laps.record'),
    length: t('motorsports.hero.statistics.length'),
    distanceUnit: t('motorsports.hero.statistics.distance.unit'),
  };
};

const getMatchStatusMap = () => {
  return {
    live: t('motorsports.hero.statistics.status.live'),
    disputed: t('motorsports.hero.statistics.status.disputed'),
  };
};

const getStatus = (status: RaceSessionStatus) => {
  const matchStatusMap = getMatchStatusMap();
  return status === RACE_STATUS_PLAYING ? (
    <BestLapComponent color="red-light" colorSvg="blue" positionSvg="right">
      {matchStatusMap.live}
    </BestLapComponent>
  ) : (
    <Text color="blue" fontSize="14--16">
      {matchStatusMap.disputed}
    </Text>
  );
};

const classNames = () =>
  makeClassNames({
    MotorsportsHero: 'motorsports-hero',
    titleWrapper: 'motorsports-hero__title-wrapper',
    heroTitle: 'motorsports-hero__title',
    dateWrapper: 'motorsports-hero__date-wrapper',
    statusWrapper: 'motorsports-hero__status-wrapper',
    circuit: 'motorsports-hero__circuit',
    winnerDriver: 'motorsports-hero__winner',
    statisticsWrapper: 'motorsports-hero__statistics-wrapper',
    statisticsTitle: 'motorsports-hero__statistics-title',
    flagImage: 'motorsports-hero__flag-image',
    statisticsItem: 'motorsports-hero__statistics-item',
    statisticsItemLabel: 'motorsports-hero__statistics-item--label',
    statisticsItemData: 'motorsports-hero__statistics-item--data',
    dateDay: 'motorsports-hero__date-day',
    dateHour: 'motorsports-hero__date-hour',
  });

const cn = classNames();

const statisticsItem = (itemData: string, itemLabel: string) => {
  return (
    <div className={cn.statisticsItem}>
      <Text className={cn.statisticsItemLabel} color="blue-light" fontSize="12">
        {itemLabel}
      </Text>
      <Text className={cn.statisticsItemData} color="blue" fontSize="14" fontWeight="500">
        {itemData}
      </Text>
    </div>
  );
};

const MotorsportsHero = ({
  flagUrl,
  customGpName,
  circuitUrl,
  startDateTime,
  winner,
  gpStatus,
  laps,
  lapRecord,
  lengthKm,
}: IMotorsportsHero) => {
  const dateFormattedDay = dateFormat({ date: startDateTime, format: 'dddd D MMMM' });
  const dateFormattedHour = dateFormat({ date: startDateTime, format: 'HH:mm' }); // h24 format!
  const statisticsLabelMap = getstatisticsLabelMap();

  return (
    <div className={cn.MotorsportsHero}>
      <div className={cn.titleWrapper}>
        {flagUrl && (
          <ImgPlaceholder
            className={cn.flagImage}
            loading="lazy"
            alt={`${customGpName} ${t('motorsports.hero.flag')}`}
            title={`${customGpName} ${t('motorsports.hero.flag')}`}
            src={flagUrl}
            placeholder={placeholdersMap[GPFLAG]}
          />
        )}
        <Text className={cn.heroTitle} color="blue" fontSize="24--32" fontWeight="500">
          {customGpName}
        </Text>
      </div>

      <div className={cn.dateWrapper}>
        <Text className={cn.dateDay} color="blue" fontSize="18--22">
          {dateFormattedDay}
        </Text>
        <Text className={cn.dateHour} color="blue" fontSize="14--16" fontWeight="500">
          {t('motorsports.hero.times.hours')} {dateFormattedHour}
        </Text>
      </div>

      <div className={cn.statusWrapper}>{gpStatus && getStatus(gpStatus)}</div>
      <div className={cn.circuit}>
        {circuitUrl && (
          <ImgPlaceholder
            loading="lazy"
            alt={`${t('motorsports.hero.circuit.image')} ${customGpName}`}
            title={`${t('motorsports.hero.circuit.image')} ${customGpName}`}
            src={circuitUrl}
            placeholder={placeholdersMap[CIRCUITTRACK]}
          />
        )}
      </div>
      <div className={cn.winnerDriver}>
        {winner && (
          <Avatar
            label="Vincitore"
            name={winner.name}
            url={winner.url}
            imageUrl={winner.imageUrl}
            placeholder={placeholdersMap[DRIVERAVATAR]}
            fontSizeName="14"
            isCircled
          />
        )}
      </div>
      <div className={cn.statisticsWrapper}>
        <Text className={cn.statisticsTitle} color="blue" fontSize="16" fontWeight="500">
          {t('motorsports.hero.statistics.title')}
        </Text>
        {laps && statisticsItem(laps, statisticsLabelMap.laps)}
        {lapRecord && statisticsItem(lapRecord, statisticsLabelMap.lapRecord)}
        {lengthKm && statisticsItem(`${lengthKm} ${statisticsLabelMap.distanceUnit}`, statisticsLabelMap.length)}
      </div>
    </div>
  );
};

export default MotorsportsHero;
